/** One dependency map for API caches and mounted ERP/supplier/logistics readers. */
const ERP_DOMAINS = ['payroll', 'customers', 'live', 'liveWork', 'purchase', 'inventory', 'products', 'suppliers', 'settings', 'orders', 'planning', 'images', 'imagePrompts', 'aiSettings', 'profile', 'design'] as const;
const LOGISTICS_DOMAINS = ['logistics-orders','logistics-products','logistics-addresses','wallet','billing','logistics-users','logistics-settings'] as const;
export const DATA_DOMAINS=[...ERP_DOMAINS,...LOGISTICS_DOMAINS] as const;
export type DataDomain = typeof DATA_DOMAINS[number];
export function domainsForUrl(url: string): DataDomain[] {
    const path = url.split('?')[0];
    if (/\/ai-settings(?:\/|$)/.test(path)) return ['aiSettings'];
    if (/\/payroll\/(access|members)$/.test(path)) return [];
    if (path.includes('/payroll/')) return ['payroll'];
    if(path.includes('/api/labeling/')){
        if(/\/(?:login|auth|logout)(?:\/|$)/.test(path))return [];
        if(/\/address(?:\/|$)/.test(path))return ['logistics-addresses'];
        if(/\/(?:(?:demo-)?products?|package)(?:\/|$)/.test(path))return ['logistics-products'];
        if(path.includes('/billing/'))return ['billing'];
        if(path.includes('/finance/'))return ['wallet'];
        if(/\/(?:order|orders|label|labels|create-order|create-label|cancel-label|batch-orders)(?:\/|$)/.test(path))return ['logistics-orders'];
        if(/\/(?:channel-code|dropshipping|rate)(?:\/|$)/.test(path))return ['logistics-settings'];
        if(path.includes('/user/'))return ['logistics-users'];
        return ['logistics-settings'];
    }
    if (/\/erp\/design\/tasks\/\d+\/session-originals\/\d+/.test(path)) return ['design','liveWork'];
    if (/\/settings-records\/complaintTypes(?:\/|$)/.test(path)) return ['customers'];
    if (/\/settings-records\//.test(path)) return ['settings'];
    if (/\/(?:customers|complaint-types)(?:\/|$)/.test(path)) return ['customers'];
    if (/\/erp\/design(?:\/|$)/.test(path)) return ['design'];
    if (/\/(?:work|work-media)(?:\/|$)/.test(path)) return ['liveWork'];
    if (/\/livestreams$/.test(path) && /[?&]view=summary(?:&|$)/.test(url)) return ['live','liveWork'];
    if (/\/livestreams\/\d+$/.test(path) && /[?&]view=(?:sessions|edit)(?:&|$)/.test(url)) return ['live','liveWork'];
    if (/\/supplier\/(?:brands|product-series|brand-profile)(?:\/|$)/.test(path)) return ['suppliers','products','settings'];
    if (/\/supplier\/(?:profile-options|locations)(?:\?|$)/.test(url)) return [];
    if (/\/(?:login|auth)(?:\/|$)/.test(path)) return [];
    if (/\/packaging\//.test(path)) return ['settings'];
    if (/\/supplier-products\/\d+\/sale-price$/.test(path)) return ['products','inventory'];
    if (/\/product-entry-media\//.test(path)) return [];
    if (/\/product-entries$/.test(path)) return ['products','inventory','purchase','live','orders','suppliers'];
    if (path.endsWith('/livestream-supplier-options')) return ['suppliers'];
    if (/\/product-selection\//.test(path)) return ['live','purchase','inventory','products','suppliers'];
    if (/\/home\/purchase-calendar$/.test(path)) return ['purchase'];
    if (/\/home\/calendar$/.test(path)) return ['live','liveWork'];
    if (/\/home\/stock$/.test(path)) return ['inventory', 'products', 'suppliers'];
    if (/\/home\//.test(path)) return ['live'];
    if (/\/supplier\/(?:me|profile)(?:\/|$)/.test(path)) return ['profile', 'suppliers'];
    if (/\/supplier\/(?:team\/)?(?:suppliers)(?:\/|$)/.test(path)) return ['suppliers', 'products', 'inventory'];
    if (/\/supplier\/.*(?:orders|items)/.test(path)) return ['orders', 'purchase', 'inventory', 'live'];
    if (/\/(?:ai-planning|pot-classification|merchandising)/.test(path)) return ['planning'];
    if (/\/image-tools\/product-configuration$/.test(path)) return ['imagePrompts'];
    if (/\/(?:image-tools|cover|poster)|(?:image-jobs|poster-jobs)/.test(path)) return ['images', 'products'];
    if (/\/(?:procurement-warehouses|warehouses|streamers|livestream-types|live-columns|product-categories|product-attributes|product-brands|pricing|settings)/.test(path)) return ['settings'];
    if (/\/procurement-suppliers/.test(path)) return ['suppliers', 'settings'];
    if (/\/returned-orders(?:\/|$)/.test(path)) return ['live'];
    if (/\/livestreams(?:\/|$)/.test(path)) return ['live'];
    if (/\/procurements(?:\/|$)/.test(path)) return ['purchase'];
    if (/\/product-prices(?:\/|$)/.test(path)) return ['products', 'inventory', 'purchase', 'live', 'suppliers'];
    if (/\/inventory(?:\/|$)/.test(path)) return ['inventory'];
    if (/\/(?:product|products|catalog|collections)(?:\/|$)/.test(path)) return ['products'];
    return [...ERP_DOMAINS]; // Unknown ERP relationships revalidate ERP readers only.
}
export function affectedDomains(url: string): DataDomain[] {
    if (/\/products\/bulk-edit\/prepare(?:\?|$)/.test(url)) return [];
    if (/\/products\/bulk-edit(?:\?|$)/.test(url)) return ['products','inventory','suppliers','purchase','live','settings'];
    if (/\/design\/tasks\/\d+\/generate(?:\?|$)/.test(url)) return ['design','liveWork'];
    if (/\/packaging\/match(?:\?|$)/.test(url)) return [];
    if (/\/returned-orders\/\d+\/fields(?:\?|$)/.test(url)) return ['live','customers'];
    if (/\/procurements\/\d+\/(view-settings|lines\/reorder)(?:\?|$)/.test(url)) return ['purchase'];
    // Presentation writes use scoped cache expiry in useLiveViewSettings, not business-wide refresh.
    if (/\/live-view-settings\//.test(url) || /\/erp\/view-settings(?:\?|$)/.test(url)) return [];
    if (/\/(?:work|work-media)(?:\/|$)/.test(url.split('?')[0])) return ['liveWork'];
    if (/\/livestreams(?:\/\d+)?\?/.test(url) && /[?&]view=(?:fields|edit)(?:&|$)/.test(url)) return ['payroll', 'live', 'purchase'];
    if (/\/livestreams\/\d+\/(?:items|groups)\/reorder(?:\?|$)/.test(url)) return ['live'];
    if (/\/livestreams\/\d+\/groups(?:\/\d+)?(?:\?|$)/.test(url)) return ['live'];
    if (/\/procurements\/\d+\?/.test(url) && /[?&]view=fields(?:&|$)/.test(url)) return ['purchase'];
    const direct = domainsForUrl(url);
    const result = new Set(direct);
    const dependents: Partial<Record<DataDomain, DataDomain[]>> = {
        'logistics-orders':['wallet','billing'],
        wallet:['logistics-orders','billing'],
        live: ['payroll', 'purchase', 'inventory', 'products', 'customers'],
        purchase: ['live', 'inventory', 'products', 'orders'],
        inventory: ['live', 'purchase', 'products'],
        products: ['live', 'purchase', 'inventory', 'suppliers'],
        suppliers: ['products', 'purchase', 'settings'],
        settings: ['payroll', 'live', 'purchase', 'inventory', 'products', 'suppliers', 'planning', 'imagePrompts'],
        orders: ['payroll', 'purchase', 'inventory', 'live'],
        images: ['products', 'inventory', 'live'],
    };
    direct.forEach(domain => dependents[domain]?.forEach(value => result.add(value)));
    return [...result];
}
export function domainsForCacheKey(key: string): DataDomain[] {
    if (key.replace(/^(?:view|page)\|/, '') === 'supplier-profile-options') return [];
    if (key.startsWith('raw|')) return domainsForUrl(key.slice(4));
    if (key.replace(/^(?:view|page)\|/, '') === 'home-stock') return ['inventory', 'products', 'suppliers'];
    const plain=key.replace(/^(?:view|page)\|/,'');
    if(plain==='planning|sources')return ['planning','purchase'];
    if(plain.startsWith('suppliers|catalog-names|'))return ['suppliers','products','inventory'];
    if(plain.startsWith('[')){
        if(plain.startsWith('["orders",'))return ['logistics-orders'];
        if(plain.startsWith('["billing-'))return ['billing'];
        if(plain.startsWith('["admin-user-overview",'))return ['logistics-users'];
    }
    if(plain.startsWith('orders|logistics-detail|'))return ['logistics-orders'];
    if(plain.startsWith('logistics|products'))return ['logistics-products'];
    if(plain.startsWith('logistics|addresses'))return ['logistics-addresses'];
    if(/^(?:navigation|catalog|list-demo)\|/.test(plain))return [];
    if(plain.startsWith('customers|products|'))return ['customers','inventory','products'];
    if(plain.startsWith('design|inputs|'))return ['live','liveWork','settings','products'];
    if(plain==='design|live-prompts')return ['design','settings'];
    if(plain==='design|screen|live')return ['design','live'];
    const name = plain.split('|')[0];
    if (plain.startsWith('live|home-calendar|')) return ['live','liveWork'];
    if (plain.startsWith('live|summary|')) return ['live','liveWork'];
    if (/^live\|sessions\|/.test(plain)) return ['live','liveWork'];
    if (DATA_DOMAINS.includes(name as DataDomain)) return [name as DataDomain];
    if (name.startsWith('supplier')) return ['suppliers', 'products', 'inventory'];
    return [...ERP_DOMAINS];
}
